import React, { useRef } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  POLICY_BIBLE_CAMPAIGN_RULES,
  POLICY_BIBLE_CHAIN_ALIASES,
  POLICY_BIBLE_GEO_ALIASES,
  type PolicyBibleCampaignRule,
} from './campaignPolicyBible';

interface Props {
  matchedRule?: PolicyBibleCampaignRule;
  open: boolean;
  onClose: () => void;
}

export default function PolicyBibleReferenceDialog({ matchedRule, open, onClose }: Props) {
  const matchedRowRef = useRef<HTMLTableRowElement>(null);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      TransitionProps={{
        onEntered: () => matchedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      }}
    >
      <DialogTitle sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', pr: 1 }}>
        <Typography fontWeight={700}>Policy Bible reference</Typography>
        <IconButton aria-label="Close Policy Bible reference" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {!matchedRule && (
            <Alert severity="warning">
              No Policy Bible entry matched this offer&apos;s chain and geo. If this chain/geo combination should be
              auto-approved, flag it to the engineering team to add or fix the entry below.
            </Alert>
          )}

          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Chain / geo thresholds
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: 'grey.50',
                      color: 'text.secondary',
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    },
                  }}
                >
                  <TableCell>Chain</TableCell>
                  <TableCell>Geo</TableCell>
                  <TableCell align="right">Date change allowed (days)</TableCell>
                  <TableCell align="right">Number of date changes</TableCell>
                  <TableCell>Educational Package Clause</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {POLICY_BIBLE_CAMPAIGN_RULES.map((rule, index) => (
                  <TableRow
                    key={`${rule.chain}-${rule.geo}-${index}`}
                    ref={rule === matchedRule ? matchedRowRef : undefined}
                    selected={rule === matchedRule}
                    sx={rule === matchedRule ? { bgcolor: 'success.50' } : undefined}
                  >
                    <TableCell>{rule.chain}</TableCell>
                    <TableCell>{rule.geo}</TableCell>
                    <TableCell align="right">{rule.dateChangeAllowedDays ?? '—'}</TableCell>
                    <TableCell align="right">{rule.numberOfDateChanges ?? '—'}</TableCell>
                    <TableCell>{rule.educationalPackageClause ? 'Enabled' : 'Not enabled'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Chain aliases
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Real Salesforce &quot;LE Marketing Terms&quot; values that resolve to a different Policy Bible chain label
              above.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Salesforce value</TableCell>
                  <TableCell>Resolves to</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(POLICY_BIBLE_CHAIN_ALIASES).map(([realValue, bibleChain]) => (
                  <TableRow key={realValue}>
                    <TableCell>{realValue}</TableCell>
                    <TableCell>{bibleChain}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Geo aliases
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Real geo value</TableCell>
                  <TableCell>Resolves to</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(POLICY_BIBLE_GEO_ALIASES).map(([realValue, bibleGeo]) => (
                  <TableRow key={realValue}>
                    <TableCell>{realValue}</TableCell>
                    <TableCell>{bibleGeo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>

          <Typography color="text.secondary" variant="caption">
            This table is defined in code (`campaignPolicyBible.ts`) and must be kept in sync with the Sales Ops
            &quot;Contracting and Assessment Information: Chains &amp; Hotel Specific&quot; Confluence page. If you spot
            a mismatch, let the engineering team know so the code can be updated.
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
